import { FC, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { DEFAULT_ITEMS_PER_PAGE } from "../../../constants/app";
import { STORAGE_KEYS } from "../../../constants/storageKeys";
import { AUTH_ROUTE } from "../../../pages/App/routes";
import {
  useAppDispatch as useDispatch,
  useAppSelector as useSelector,
} from "../../../store";
import {
  hideTransparentPreloaderAction,
  showTransparentPreloaderAction,
} from "../../../store/application/slice";
import { getCandidateDataForCentreThunk } from "../../../store/centers/thunks";
import {
  getVacancyHistory,
  toggleCandidatePreferredStatus
} from "../../../store/vacancy/thunks";
import {
  HistoryVacancySortField,
  SortOrder,
  TVacancyDetail,
} from "../../../store/vacancy/types";
import storage from "../../../utils/storage";
import PaginationControls from "../../Pagination/PaginationControls";
import PopupModal from "../../UI/Popup";
import ViewProfile from "../Candidates/PopupModal/ViewProfile";
import HistoryCard from "./History/HistoryCard";
import HistoryTableHeader from "./History/HistoryTableHeader";
import ConfirmBlock from "./Popup/ConfirmBlock";
import RatingCandidate from "./Popup/RatingCandidate";
import TabsHeader from "./TabsHeader";

type ModalType = "RATING" | "CANCEL" | null;

const VacanciesHistory: FC = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    historyVacancies,
    pagination,

    totalPendingVacancies,
    totalAcceptedVacancies,
  } = useSelector((state) => state.vacancy.history);
  const isShowNotification = useSelector((state) => state.app.isShowNotification)
  // Local states for sorting
  const [sortBy, setSortBy] = useState<HistoryVacancySortField>("timeframe");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");

  // Pagination states
  const [page, setPage] = useState<number>(1);
  const [perPage, setPerPage] = useState<number>(DEFAULT_ITEMS_PER_PAGE);
  const [historyDropdownId, setHistoryDropdownId] = useState<number | null>(
    null
  );

  const [showProfileModal, setShowProfileModal] = useState<boolean>(false);
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [selectedVacancy, setSelectedVacancy] = useState<TVacancyDetail | null>(
    null
  );

  const selectedCentreDetails = useSelector(
    (state) => state.vacancy.selectedCentreDetails
  );

  const [showConfirmBlockPopup, setShowConfirmBlockPopup] = useState(false);
  const [blockList, setBlockList] = useState([]);

  const handleRating = (vacancy: TVacancyDetail) => {
    setSelectedVacancy(vacancy);
    setHistoryDropdownId(null);
    setActiveModal("RATING");
  };

  const onClickClose = () => {
    setActiveModal(null);
    setSelectedVacancy(null);
    setShowProfileModal(false);
  };

  // Handle sorting logic
  const handleSortChange = (field: HistoryVacancySortField) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(field);
      setSortOrder("asc");
    }
  };

  // Handle pagination link click
  const handlePageChange = (url: string | null) => {
    if (url) {
      const page = new URL(url).searchParams.get("page");
      setPage(Number(page));
    }
  };

  const handleViewVacancy = async (vacancy: TVacancyDetail) => {
    storage.set(STORAGE_KEYS.SELECTED_VACANCY_ID, vacancy.uuid);
    navigate(AUTH_ROUTE.PENDING_VACANCY);
  };

  const handleDuplicateVacancy = (vacancy: TVacancyDetail) => {
    storage.set(STORAGE_KEYS.DUPLICATE_VACANCY_ID, vacancy.uuid);
    navigate(AUTH_ROUTE.CREATE_VACANCY);
  };

  // Fetch vacancy history list (sorted, paginated)
  const fetchVacancyHistoryist = useCallback(async () => {
    dispatch(showTransparentPreloaderAction());

    dispatch(
      getVacancyHistory({
        page,
        per_page: perPage,
        sort_by: sortBy,
        sort_order: sortOrder,
        centre_uuid:
          selectedCentreDetails !== null
            ? selectedCentreDetails
            : storage.get(STORAGE_KEYS.SELECTED_CENTRE_UUID),
      })
    ).finally(() => {
      dispatch(hideTransparentPreloaderAction());
    });
  }, [dispatch, page, perPage, sortBy, sortOrder, selectedCentreDetails]);

  // Call fetch function on filter/pagination/sort change
  useEffect(() => {
    if(!isShowNotification) {
      fetchVacancyHistoryist();
    }
  }, [fetchVacancyHistoryist, page, perPage, sortBy, sortOrder, isShowNotification]);

  const handleCandidateProfileView = async (vacancy: TVacancyDetail) => {
    dispatch(showTransparentPreloaderAction());
    await dispatch(
      getCandidateDataForCentreThunk({
        candidate_id: String(vacancy.selected_candidate.uuid),
        centre_id: String(vacancy.centre_id),
      })
    );
    dispatch(hideTransparentPreloaderAction());
    setShowProfileModal(!false);
  };

  const showBlockConfirmationModal = (candidatesToBlock: any) => {
    setBlockList(candidatesToBlock);
    setShowConfirmBlockPopup(true);
  };

  const hideBlockConfirmationModal = () => {
    setShowConfirmBlockPopup(false);
  };

  const handleCandidatePreferred = (vacancy: TVacancyDetail) => {
    dispatch(
      toggleCandidatePreferredStatus({
        centre_id: vacancy.centre_id,
        centre_uuid: vacancy.centre.uuid,
        user_id: vacancy.selected_candidate!.id,
        user_uuid: vacancy.selected_candidate!.uuid,
        action: vacancy.selected_candidate.is_preferred_candidate
          ? "remove"
          : "mark",
        is_preferred: vacancy.selected_candidate.is_preferred_candidate
          ? false
          : true,
      })
    );
  };

  return (
    <>
      <div className="d-flex flex-column">
        <TabsHeader activeHeader={"History"} />
        <HistoryTableHeader
          sortBy={sortBy}
          sortOrder={sortOrder}
          onSortChange={handleSortChange}
        />
        {historyVacancies.length > 0 ? (
          historyVacancies.map((vacancy: TVacancyDetail) => (
            <HistoryCard
              key={vacancy.id}
              vacancy={vacancy}
              handleRating={() => handleRating(vacancy)}
              showDropdown={historyDropdownId === vacancy.id}
              setShowDropdown={(val) =>
                setHistoryDropdownId(val ? vacancy.id : null)
              }
              onViewDetails={() => handleViewVacancy(vacancy)}
              handleDuplicate={() => handleDuplicateVacancy(vacancy)}
              handleProfileView={() => handleCandidateProfileView(vacancy)}
              handlePreferred={() => handleCandidatePreferred(vacancy)}
            />
          ))
        ) : (
          <div className="d-flex gap-20 align-items-center justify-content-center text-center p-15px">
            <div className="fs-16 fw-400 text-grey-medium">
              No vacancies found in history.
            </div>
          </div>
        )}
        {/* Render pagination controls if there are more than 10 vacancies history */}
        {pagination && pagination.total > DEFAULT_ITEMS_PER_PAGE && (
          <PaginationControls
            currentPage={page} // Current page number
            totalPages={pagination?.last_page} // Total number of pages
            paginationLinks={pagination?.links} // List of pagination links
            onPageChange={handlePageChange} // Function to handle page change
            setPerPageList={setPerPage} // Function to set the number of items per page
            setCurrentPage={setPage} // Function to set the current page
          />
        )}
      </div>
      {/* Rating Modal */}
      <PopupModal
        visible={!!activeModal}
        showCloseBtn={true}
        onClickClose={onClickClose}
        customWidth={650}
      >
        {/* Rating Modal */}
        {activeModal === "RATING" && (
          <RatingCandidate
            vacancy={selectedVacancy as TVacancyDetail}
            showConfirmBlock={(candidatesToBlock: any) =>
              showBlockConfirmationModal(candidatesToBlock)
            }
            onClose={onClickClose}
          />
        )}
      </PopupModal>
      {showConfirmBlockPopup && (
        <ConfirmBlock
          showBlockModal={showConfirmBlockPopup}
          candidatesToBlock={blockList}
          onClose={hideBlockConfirmationModal}
        />
      )}

      <ViewProfile
        showProfileModal={showProfileModal}
        onClickClose={onClickClose}
      />
    </>
  );
};

export default VacanciesHistory;
